import React, { useEffect, useRef, useState } from 'react';
import CanvasScrollSequence from './CanvasScrollSequence';

interface ScrollVideoBackgroundProps {
  src: string;
  poster: string;
  /** CSS gradient/overlay painted on top of the video for text legibility. */
  overlay?: string;
  /**
   * Fraction of the section's own height over which the clip scrubs from 0..1.
   * 1 = clip finishes exactly as the section leaves the viewport. Smaller = faster.
   */
  scrub?: number;
  /** Frame sequence used for scroll-scrubbing on touch devices (iOS can't seek video). */
  framePrefix?: string;
  frameCount?: number;
}

const matches = (q: string) => typeof window.matchMedia === 'function' && window.matchMedia(q).matches;
// reduced-motion -> static; touch/coarse pointer -> canvas frame-scrub (video seek
// is unreliable there); otherwise -> scrubbed <video> on desktop.
const pickMode = (hasFrames: boolean): 'static' | 'canvas' | 'video' => {
  if (matches('(prefers-reduced-motion: reduce)')) return 'static';
  if (hasFrames && matches('(pointer: coarse)')) return 'canvas';
  return 'video';
};

/**
 * Background clip behind a (position: relative) section, scrubbed by scroll.
 * Desktop seeks an all-intra <video>; touch devices draw a preloaded frame
 * sequence on canvas (works on iOS); reduced-motion shows a static poster.
 */
const ScrollVideoBackground: React.FC<ScrollVideoBackgroundProps> = ({
  src,
  poster,
  overlay,
  scrub = 1,
  framePrefix,
  frameCount,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const rafRef = useRef<number>(0);
  const [mode] = useState(() => pickMode(Boolean(framePrefix && frameCount)));

  useEffect(() => {
    if (mode !== 'video') return;

    const apply = () => {
      const v = videoRef.current;
      if (!v) return;
      const d = v.duration;
      if (!d || !isFinite(d) || v.readyState < 1) return;
      const rect = v.getBoundingClientRect();
      const span = rect.height * Math.max(0.2, scrub);
      const p = Math.min(1, Math.max(0, -rect.top / span));
      const t = p * d;
      if (Math.abs(v.currentTime - t) > 0.02) v.currentTime = t;
    };

    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        apply();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    const v = videoRef.current;
    const primeVideo = () => {
      if (!v) return;
      const pr = v.play();
      if (pr && typeof pr.then === 'function') pr.then(() => v.pause()).catch(() => {});
      onScroll();
    };
    if (v) {
      v.addEventListener('loadedmetadata', onScroll);
      v.addEventListener('loadeddata', primeVideo, { once: true });
    }
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (v) {
        v.removeEventListener('loadedmetadata', onScroll);
        v.removeEventListener('loadeddata', primeVideo);
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };
  }, [scrub, mode]);

  const renderOverlay = () =>
    overlay && (
      <div
        aria-hidden
        style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', background: overlay }}
      />
    );

  const layer: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    zIndex: 0,
    transform: 'scale(1.06)',
    pointerEvents: 'none',
  };

  if (mode === 'canvas') {
    return (
      <>
        <div style={layer}>
          <CanvasScrollSequence framePrefix={framePrefix!} frameCount={frameCount!} scrub={scrub} />
        </div>
        {renderOverlay()}
      </>
    );
  }

  if (mode === 'static') {
    return (
      <>
        <div
          aria-hidden
          style={{ ...layer, backgroundImage: `url(${poster})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        />
        {renderOverlay()}
      </>
    );
  }

  return (
    <>
      <video ref={videoRef} muted playsInline preload="auto" poster={poster} aria-hidden style={{ ...layer, objectFit: 'cover' }}>
        <source src={src} type="video/mp4" />
      </video>
      {renderOverlay()}
    </>
  );
};

export default ScrollVideoBackground;
